import React, {useState} from "react";
import {
    Autocomplete,
    Box,
    Button,
    Checkbox, IconButton,
    Paper,
    Stack,
    TextField, Tooltip,
    Typography
} from "@mui/material";
import CustomRichTextEditor from "../../components/input/CustomRichTextEditor";
import {useNavigate} from "react-router-dom";
import {useNotification} from "../../utils/hooks/useNotification";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {getFetcher, postFetcher, putFetcher} from "../../utils/api/fetchers";
import {RaceCreateRequest, raceCreateSchema} from "../../schemas/races/raceCreateSchema";
import {PublishStatus, Race} from "../../schemas/races/racesSchema";
import {DatePicker, LocalizationProvider, TimePicker} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import useSWR from "swr";
import {Club} from "../../schemas/club/clubSchema";
import {Season} from "../../schemas/seasons/seasonsSchema";
import {UserAccount} from "../../schemas/user/userAccountSchemas";
import UserRole from "../../schemas/user/roles";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DownloadIcon from '@mui/icons-material/Download';
import ClearIcon from '@mui/icons-material/Clear';
import RacePublishDialog from "../../components/dialog/RacePublishDialog";
import {isFileTypeAllowed} from "../../utils/races/checkAllowedFileType";

const RaceCreate: React.FC = () => {
    const navigate = useNavigate();
    const {showNotification} = useNotification();
    const {register, handleSubmit, watch, setValue, formState: {errors}, trigger} = useForm<RaceCreateRequest>({
        resolver: zodResolver(raceCreateSchema),
        defaultValues: {
            clubId: null,
            rules: undefined,
            organizersIds: [],
            raceSeasonId: undefined
        }
    });
    const {data: clubs} = useSWR<Club[]>("/clubs/all", getFetcher);
    const {data: seasons} = useSWR<Season[]>("/seasons/all", getFetcher);
    const {data: organisers} = useSWR<UserAccount[]>(`/roles/${UserRole.ORGANIZER}/users`, getFetcher);
    const [attachments, setAttachments] = useState<File[]>([]);
    const [formData, setFormData] = useState<RaceCreateRequest | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

    const onSubmit = async (data: RaceCreateRequest) => {
        setFormData(data);
        setIsDialogOpen(true);
    };

    const handlePublishConfirm = async (status: PublishStatus) => {
        setIsDialogOpen(false);
        if (!formData) return;

        let createdRace: Race | null = null;

        // Race creation
        try {
            createdRace = await postFetcher<RaceCreateRequest, Race>("/races", formData);
        } catch (error: any) {
            console.error("Chyba při vytváření závodu:", error);
            if (error.response?.status === 409) {
                showNotification("Závod s tímto jménem a datem již existuje", "error");
            } else {
                showNotification("Nepodařilo se vytvořit závod", "error");
            }
            return;
        }

        // Attachments upload
        if (attachments.length > 0) {
            const failedFiles: string[] = [];

            for (const file of attachments) {
                const formDataObj = new FormData();
                formDataObj.append("file", file);

                try {
                    await postFetcher<FormData, void>(
                        `/races/${createdRace.id}/attachments`,
                        formDataObj,
                        {headers: {'Content-Type': 'multipart/form-data'}}
                    );
                } catch (error: any) {
                    console.error(`Chyba při nahrávání souboru ${file.name}:`, error);
                    failedFiles.push(file.name);
                }
            }

            if (failedFiles.length > 0) {
                showNotification(`Závod byl vytvořen, ale některé přílohy se nepodařilo nahrát: ${failedFiles.join(', ')}`, "warning");
            }
        }

        // Publish status update
        try {
            await putFetcher(`/races/${createdRace.id}/status/publish`, {publishStatus: status});
        } catch (error: any) {
            console.error("Chyba při nastavování stavu publikace:", error);
            showNotification("Závod byl vytvořen, ale nepodařilo se změnit stav publikace", "warning");
            return;
        }

        showNotification("Závod byl úspěšně vytvořen", "success");
        navigate('/races');
    };

    const handleAttachmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            const newFiles = Array.from(files);

            const validFiles = newFiles.filter(file => isFileTypeAllowed(file.type));
            if (validFiles.length < newFiles.length) {
                showNotification("Některé soubory nebyly přidány – povolené typy jsou PDF a obrázky", "warning");
            }

            setAttachments((prev) => [...prev, ...validFiles]);
            event.target.value = ''; // reset input value to allow re-uploading the same file
        }
    };

    const handleAttachmentDelete = (index: number) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
    };

    return (
        <Paper elevation={3} sx={{padding: 4, maxWidth: 800, margin: '0 auto'}}>
            <Typography variant="h4" gutterBottom>
                Vytvoření závodu
            </Typography>
            <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={3}>
                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={1}>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Jméno"
                            variant="standard"
                            required
                            {...register('name')}
                            error={!!errors.name}
                            helperText={errors.name?.message}
                        />
                        <Autocomplete
                            fullWidth
                            options={seasons ?? []}
                            getOptionLabel={(option) => option.year.toString()}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => <TextField
                                {...params}
                                label="Sezóna"
                                variant="standard"
                                error={!!errors.raceSeasonId}
                                helperText={errors.raceSeasonId?.message}
                                inputRef={register('raceSeasonId').ref}
                                required
                            />}
                            onChange={(_, value) => {
                                if (value) setValue('raceSeasonId', value.id, {shouldDirty: true, shouldValidate: true})
                            }}
                            onBlur={() => trigger('raceSeasonId')}
                            disableClearable
                            sx={{width: {xs: '100%', sm: '35%'}}}
                        />
                    </Stack>
                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={1} justifyContent="space-between">
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="cs">
                            <DatePicker
                                label="Datum"
                                format="DD.MM.YYYY"
                                {...register('date')}
                                onChange={(value) => {
                                    if (value?.isValid()) {
                                        setValue('date', value.format("YYYY-MM-DD"), {
                                            shouldDirty: true,
                                            shouldValidate: true
                                        });
                                    }
                                }}
                                inputRef={register('date').ref}
                                slotProps={{
                                    textField: {
                                        variant: 'standard',
                                        fullWidth: true,
                                        error: !!errors.date,
                                        helperText: errors.date?.message,
                                        required: true,
                                        onBlur: () => trigger('date')
                                    },
                                    field: {clearable: false}
                                }}
                            />
                            <TimePicker
                                label="Čas startu"
                                {...register('time')}
                                onChange={(value) => {
                                    setValue('time', value?.format("HH:mm:ss"), {
                                        shouldDirty: true,
                                        shouldValidate: true
                                    })
                                }}
                                inputRef={register('time').ref}
                                slotProps={{
                                    textField: {
                                        variant: 'standard', fullWidth: true,
                                        error: !!errors.time,
                                        helperText: errors.time?.message,
                                        onBlur: () => trigger('time')
                                    },
                                    field: {clearable: true}
                                }}
                            />
                        </LocalizationProvider>
                    </Stack>
                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={1} justifyContent="space-between">
                        <TextField
                            autoFocus
                            fullWidth
                            label="Místo konání"
                            variant="standard"
                            required
                            {...register('location')}
                            error={!!errors.location}
                            helperText={errors.location?.message}
                        />
                        <Autocomplete
                            fullWidth
                            options={clubs ?? []}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => <TextField
                                {...params}
                                label="Pořádající klub"
                                variant="standard"
                                error={!!errors.clubId}
                                helperText={errors.clubId?.message}
                                inputRef={register('clubId').ref}
                            />}
                            onChange={(_, value) => setValue('clubId', value ? Number(value.id) : undefined, {
                                shouldDirty: true,
                                shouldValidate: true
                            })}
                            onBlur={() => trigger('clubId')}
                        />
                    </Stack>
                    <CustomRichTextEditor
                        title="Popis"
                        value={watch('description') ?? undefined}
                        onChange={(value) => setValue('description', value, {shouldDirty: true, shouldValidate: true})}
                        error={errors.description?.message}
                    />
                    <Autocomplete
                        multiple
                        limitTags={2}
                        options={organisers ?? []}
                        getOptionLabel={(option) => `${option.firstName} ${option.lastName} - ${option.email}`}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => <TextField
                            {...params}
                            label="Pořadatelé"
                            variant="standard"
                            error={!!errors.organizersIds}
                            helperText={errors.organizersIds?.message}
                            inputRef={register('organizersIds').ref}
                        />}
                        disableCloseOnSelect
                        renderOption={(props, option, {selected}) => {
                            const {key, ...optionProps} = props;
                            return (
                                <li key={key} {...optionProps}>
                                    <Checkbox
                                        icon={<CheckBoxOutlineBlankIcon/>}
                                        checkedIcon={<CheckBoxIcon/>}
                                        style={{marginRight: 8}}
                                        checked={selected}
                                    />
                                    {option.firstName} {option.lastName} - {option.email}
                                </li>
                            );
                        }}
                        onChange={(_, value) => setValue('organizersIds', value.map((v) => v.id), {
                            shouldDirty: true,
                            shouldValidate: true
                        })}
                        onBlur={() => trigger('organizersIds')}
                    />
                    <Stack direction="column" spacing={0} alignItems="flex-start">
                        <Typography>Přílohy:</Typography>

                        {attachments.map((file, index) => (
                            <Stack
                                key={index}
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                            >
                                <Tooltip title="Zobrazit přílohu" arrow placement="top">
                                    <Typography
                                        variant="body2"
                                        onClick={() => {
                                            const url = URL.createObjectURL(file);
                                            window.open(url, "_blank");
                                        }}
                                        sx={{
                                            textDecoration: 'underline',
                                            cursor: 'pointer',
                                            '&:hover': {color: 'primary.main'}
                                        }}
                                    >
                                        {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                    </Typography>
                                </Tooltip>

                                <IconButton
                                    title="Stáhnout přílohu"
                                    size="small"
                                    onClick={() => {
                                        const url = URL.createObjectURL(file);
                                        const link = document.createElement("a");
                                        link.href = url;
                                        link.download = file.name;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                        URL.revokeObjectURL(url);
                                    }}
                                >
                                    <DownloadIcon/>
                                </IconButton>

                                <IconButton
                                    title="Odstranit přílohu"
                                    size="small"
                                    color="error"
                                    onClick={() => handleAttachmentDelete(index)}
                                >
                                    <ClearIcon/>
                                </IconButton>
                            </Stack>
                        ))}

                        <Button variant="outlined" component="label" color="success">
                            Přidat přílohu
                            <input
                                type="file"
                                hidden
                                multiple
                                accept="application/pdf, image/*"
                                onChange={handleAttachmentChange}
                            />
                        </Button>
                    </Stack>
                    <Button type="submit" variant="contained" color="primary" fullWidth>
                        Vytvořit a publikovat závod
                    </Button>
                </Stack>
            </Box>
            <RacePublishDialog
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                onConfirm={handlePublishConfirm}
                newRace={true}
            />
        </Paper>
    );
}

export default RaceCreate;